// Tabs
class TabsCustom extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #panels = [];

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#buildTabs();
    this.#activateDefaultTab();

    const tablist = this.querySelector('[role="tablist"]');

    if (tablist) {
      tablist.addEventListener('keydown', this.#handleKeyDown, { signal });
    }
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #buildTabs() {
    const tablist = this.querySelector('.tabs__list');
    const panels = this.querySelectorAll('.tabs__panel[data-tabs-label]');

    if (!(tablist instanceof HTMLElement) || panels.length === 0) return;

    tablist.replaceChildren();
    this.#tabs = [];
    this.#panels = [];

    const { signal } = this.#controller;

    panels.forEach((panel, index) => {
      if (!(panel instanceof HTMLElement)) return;

      const label = panel.dataset.tabsLabel || `Tab ${index + 1}`;
      const panelId = panel.id || `TabPanel-${index}`;

      if (!panel.id) panel.id = panelId;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'tabs__trigger';
      tab.role = 'tab';
      tab.id = `Tab-${panelId}`;
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', 'false');
      tab.tabIndex = -1;
      tab.textContent = label;

      tab.addEventListener('click', () => this.#activateTab(index), { signal });

      tablist.appendChild(tab);
      this.#tabs.push(tab);
      this.#panels.push(panel);
    });
  }

  #activateDefaultTab() {
    const defaultIndex = this.#panels.findIndex((panel) => panel.hasAttribute('data-open-by-default'));
    this.#activateTab(defaultIndex >= 0 ? defaultIndex : 0);
  }

  /**
   * @param {number} index
   */
  #activateTab(index) {
    this.#tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.#panels.forEach((panel, panelIndex) => {
      if (panelIndex === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const currentIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');

    if (currentIndex === -1) return;

    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        newIndex = currentIndex <= 0 ? this.#tabs.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        newIndex = currentIndex >= this.#tabs.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        newIndex = 0;
        break;
      case 'End':
        newIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activateTab(newIndex);
    this.#tabs[newIndex]?.focus();
  };
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
